import { useEffect, useMemo, useRef, useState } from "react";
import {
    Archive,
    Check,
    ChevronDown,
    FolderInput,
    Mail,
    MailOpen,
    Paperclip,
    RefreshCw,
    Search,
    Star,
    Trash2,
    X,
} from "lucide-react";
import { Skeleton } from "@discordforge/ui";
import type { Folder, MessageListItem } from "../types";
import { formatDate, formatSize, senderName } from "../utils";

export interface MessageListPaneProps {
    folder: Folder;
    messages: MessageListItem[];
    total: number;
    loading: boolean;
    error: string;
    selectedUid: number | null;
    onSelect: (message: MessageListItem) => void;
    onRefresh: () => void;
    hasMore?: boolean;
    loadingMore?: boolean;
    onLoadMore?: () => void;
    search?: string;
    onSearchChange?: (query: string) => void;
    /* ---- message actions (target = checked, else selected) ---- */
    onDelete: (uids: number[]) => void;
    onArchive: (uids: number[]) => void;
    onMove: (uids: number[], toFolder: string) => void;
    onToggleFlag: (uids: number[], flagged: boolean) => void;
    onMarkRead: (uids: number[], read: boolean) => void;
    /** Folders a message can be moved to (excludes the current one). */
    moveTargets: Folder[];
    /** When in Trash, Delete = hard delete (upstream behaviour). */
    inTrash: boolean;
    /** Whether an Archive folder exists (hide the button when it doesn't). */
    archiveAllowed?: boolean;
}

const TOOLBAR_BTN =
    "flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-400 transition-colors hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-30";

export function MessageListPane({
    folder,
    messages,
    total,
    loading,
    error,
    selectedUid,
    onSelect,
    onRefresh,
    hasMore = false,
    loadingMore = false,
    onLoadMore,
    search = "",
    onSearchChange,
    onDelete,
    onArchive,
    onMove,
    onToggleFlag,
    onMarkRead,
    moveTargets,
    inTrash,
    archiveAllowed = true,
}: MessageListPaneProps) {
    const [checked, setChecked] = useState<Set<number>>(new Set());
    const [moveOpen, setMoveOpen] = useState(false);
    const moveRef = useRef<HTMLDivElement>(null);

    /* ---- debounced search ---- */
    const [input, setInput] = useState(search);
    const inputRef = useRef<HTMLInputElement>(null);
    const isSearching = search.length > 0;

    // Sync the local input when the active search changes externally.
    useEffect(() => setInput(search), [search]);

    // Debounce keystrokes; skip when nothing actually changed.
    useEffect(() => {
        const t = setTimeout(() => {
            if (input !== search) onSearchChange?.(input);
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input]);

    const clearSearch = () => {
        setInput("");
        onSearchChange?.("");
        inputRef.current?.focus();
    };

    /* Close the move-menu on outside click. */
    useEffect(() => {
        if (!moveOpen) return;
        const onDown = (e: MouseEvent) => {
            if (!moveRef.current?.contains(e.target as Node)) setMoveOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [moveOpen]);

    const toggleAll = () => {
        setChecked((prev) => (prev.size === messages.length ? new Set() : new Set(messages.map((m) => m.uid))));
    };
    const toggleOne = (uid: number) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid);
            else next.add(uid);
            return next;
        });
    };

    /* Target of toolbar actions: checked messages, else the selected one. */
    const targetUids = useMemo(() => {
        if (checked.size) return [...checked];
        return selectedUid !== null ? [selectedUid] : [];
    }, [checked, selectedUid]);
    const targetMessages = useMemo(() => {
        const set = new Set(targetUids);
        return messages.filter((m) => set.has(m.uid));
    }, [targetUids, messages]);
    const targetFlagged =
        targetMessages.length > 0 && targetMessages.every((m) => m.flags.includes("\\flagged"));
    const targetUnseen =
        targetMessages.length > 0 && targetMessages.some((m) => !m.flags.includes("\\seen"));
    const hasTarget = targetUids.length > 0 && targetMessages.length > 0;

    const run = (fn: (uids: number[]) => void, clear = true) => {
        if (!targetUids.length) return;
        fn(targetUids);
        if (clear) setChecked(new Set());
    };

    const unread = useMemo(() => messages.filter((m) => !m.flags.includes("\\seen")).length, [messages]);

    return (
        <section className="flex h-full min-w-0 flex-1 flex-col border-r border-white/10 bg-[#0b0b0f]">
            {/* Toolbar */}
            <div className="flex shrink-0 items-center gap-3 border-b border-white/10 px-4 py-2.5">
                <button
                    type="button"
                    onClick={toggleAll}
                    className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-[5px] border-[1.5px] border-white/20 bg-black/20 transition hover:border-orange-400/60"
                    title={checked.size ? "Uncheck all" : "Check all"}
                >
                    {checked.size > 0 && <Check size={12} strokeWidth={3.5} className="text-orange-400" />}
                </button>
                <div className="flex items-baseline gap-2">
                    <h2 className="text-sm font-black tracking-tight text-white">{folder.name}</h2>
                    <span className="text-xs tabular-nums text-zinc-500">
                        {loading ? "…" : `${messages.length}${total > messages.length ? ` of ${total}` : ""}`}
                        {unread > 0 && <span className="ml-1.5 font-bold text-orange-400">· {unread} unread</span>}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onRefresh}
                    title="Refresh"
                    className="ml-auto cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
            </div>

            {/* Actions (enabled when something is selected/checked) */}
            <div className="flex shrink-0 items-center gap-1 border-b border-white/10 px-2 py-1.5">
                {!hasTarget ? (
                    <span className="px-2 text-[11px] text-zinc-600">
                        Select messages to delete, archive or move…
                    </span>
                ) : (
                    <>
                        {!inTrash && (
                            <button type="button" className={TOOLBAR_BTN} onClick={() => run(onDelete)} title="Move to Trash">
                                <Trash2 size={13} /> Delete
                            </button>
                        )}
                        {inTrash && (
                            <button type="button" className={TOOLBAR_BTN} onClick={() => run(onDelete)} title="Delete forever">
                                <Trash2 size={13} /> Delete forever
                            </button>
                        )}
                        {!inTrash && archiveAllowed && (
                            <button type="button" className={TOOLBAR_BTN} onClick={() => run(onArchive)} title="Move to Archive">
                                <Archive size={13} /> Archive
                            </button>
                        )}
                        <div className="relative" ref={moveRef}>
                            <button
                                type="button"
                                className={TOOLBAR_BTN}
                                onClick={() => setMoveOpen((v) => !v)}
                                disabled={moveTargets.length === 0}
                                title="Move to folder"
                            >
                                <FolderInput size={13} /> Move <ChevronDown size={11} />
                            </button>
                            {moveOpen && (
                                <div className="absolute left-0 top-full z-50 mt-1 max-h-60 w-52 overflow-y-auto rounded-xl border border-white/10 bg-[#141419] p-1 shadow-[0_15px_40px_rgba(0,0,0,0.7)]">
                                    {moveTargets.map((f) => (
                                        <button
                                            key={f.fullName}
                                            type="button"
                                            onClick={() => {
                                                setMoveOpen(false);
                                                run((uids) => onMove(uids, f.fullName));
                                            }}
                                            className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-zinc-300 transition-colors hover:bg-white/5 hover:text-white"
                                        >
                                            <FolderInput size={12} className="shrink-0 text-zinc-500" />
                                            <span className="truncate">{f.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className={TOOLBAR_BTN}
                            onClick={() => run((uids) => onToggleFlag(uids, !targetFlagged), false)}
                            title={targetFlagged ? "Unflag" : "Flag"}
                        >
                            <Star size={13} className={targetFlagged ? "fill-amber-400 text-amber-400" : ""} />
                            {targetFlagged ? "Unflag" : "Flag"}
                        </button>
                        <button
                            type="button"
                            className={TOOLBAR_BTN}
                            onClick={() => run((uids) => onMarkRead(uids, targetUnseen), false)}
                            title={targetUnseen ? "Mark as read" : "Mark as unread"}
                        >
                            {targetUnseen ? <MailOpen size={13} /> : <Mail size={13} />}
                            {targetUnseen ? "Read" : "Unread"}
                        </button>
                        <span className="ml-auto px-2 text-[11px] tabular-nums text-zinc-500">
                            {targetUids.length} selected
                        </span>
                    </>
                )}
            </div>

            {/* Search */}
            <div className="flex shrink-0 items-center gap-2 border-b border-white/10 px-3 py-2">
                <div className="relative flex min-w-0 flex-1 items-center">
                    <Search
                        size={13}
                        className={`pointer-events-none absolute left-2.5 ${isSearching ? "text-orange-400" : "text-zinc-500"}`}
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape" && input) {
                                e.stopPropagation();
                                clearSearch();
                            }
                        }}
                        placeholder="Search mail…"
                        aria-label="Search mail"
                        className="w-full rounded-lg border border-white/10 bg-black/20 py-1.5 pl-8 pr-7 text-xs text-zinc-200 outline-none transition-all placeholder:text-zinc-600 focus:border-orange-400/50 focus:bg-black/30 focus:ring-1 focus:ring-orange-400/30"
                    />
                    {input && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            title="Clear search"
                            className="absolute right-1.5 cursor-pointer rounded p-0.5 text-zinc-500 transition-colors hover:bg-white/10 hover:text-white"
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                {loading && messages.length === 0 ? (
                    <div className="flex flex-col gap-2.5 p-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-3 px-2">
                                <Skeleton className="h-4 w-4" radius="sm" />
                                <div className="flex flex-1 flex-col gap-1.5">
                                    <Skeleton className="h-3 w-1/3" />
                                    <Skeleton className="h-3 w-2/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="flex h-full items-center justify-center p-6 text-center">
                        <p className="text-sm font-medium text-red-400">{error}</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-2xl opacity-60">
                            {isSearching ? "🔍" : "📭"}
                        </div>
                        {isSearching ? (
                            <>
                                <div className="text-sm font-bold text-zinc-300">No results</div>
                                <p className="max-w-xs text-xs leading-relaxed text-zinc-500">
                                    Nothing matched “{search}”. Try a different search term.
                                </p>
                            </>
                        ) : (
                            <>
                                <div className="text-sm font-bold text-zinc-300">This folder is empty</div>
                                <p className="max-w-xs text-xs leading-relaxed text-zinc-500">
                                    Messages sent to this address will appear here.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <ul className="flex flex-col">
                        {messages.map((msg) => {
                            const isUnseen = !msg.flags.includes("\\seen");
                            const isFlagged = msg.flags.includes("\\flagged");
                            const isSelected = msg.uid === selectedUid;
                            const isChecked = checked.has(msg.uid);
                            return (
                                <li
                                    key={msg.uid}
                                    data-message-row
                                    data-unread={isUnseen ? "1" : "0"}
                                    onClick={() => onSelect(msg)}
                                    className={`group relative flex cursor-pointer items-center gap-3 border-b border-white/[0.04] px-4 py-2.5 transition-colors ${
                                        isSelected
                                            ? "bg-orange-500/[0.08]"
                                            : isChecked
                                              ? "bg-white/[0.04]"
                                              : "hover:bg-white/[0.03]"
                                    }`}
                                >
                                    {/* unread indicator */}
                                    {isUnseen && (
                                        <span className="absolute left-0 top-0 h-full w-[3px] rounded-r bg-gradient-to-b from-orange-500 to-amber-500" />
                                    )}

                                    {/* checkbox */}
                                    <span
                                        role="checkbox"
                                        aria-checked={isChecked}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleOne(msg.uid);
                                        }}
                                        className={`flex h-[18px] w-[18px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border-[1.5px] transition duration-150 ${
                                            isChecked
                                                ? "border-orange-400 bg-[linear-gradient(135deg,#ff9500,#ff6b00)] shadow-[0_0_12px_rgba(255,140,0,0.4)]"
                                                : "border-white/20 bg-black/20 opacity-0 group-hover:opacity-100"
                                        }`}
                                    >
                                        {isChecked && <Check size={12} strokeWidth={3.5} className="text-black" />}
                                    </span>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <span
                                                className={`truncate text-[13px] ${
                                                    isUnseen
                                                        ? "font-bold text-white"
                                                        : "font-medium text-zinc-300"
                                                }`}
                                            >
                                                {senderName(msg.from)}
                                            </span>
                                            <span className="flex shrink-0 items-center gap-1.5 text-[11px] tabular-nums text-zinc-500">
                                                {isFlagged && (
                                                    <Star size={11} className="fill-amber-400 text-amber-400" />
                                                )}
                                                {formatDate(msg.dateTimestamp)}
                                            </span>
                                        </div>
                                        <div className="mt-0.5 flex items-center justify-between gap-3">
                                            <span
                                                className={`truncate text-xs ${
                                                    isUnseen ? "font-semibold text-zinc-200" : "text-zinc-500"
                                                }`}
                                            >
                                                {msg.subject || "(no subject)"}
                                            </span>
                                            <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-zinc-600">
                                                {msg.attachments["@Collection"].length > 0 && (
                                                    <Paperclip size={11} className="text-zinc-500" />
                                                )}
                                                {formatSize(msg.size)}
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                        {hasMore && (
                            <li className="p-2">
                                <button
                                    type="button"
                                    onClick={onLoadMore}
                                    disabled={loadingMore}
                                    className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-xs font-semibold text-zinc-300 transition-all hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {loadingMore ? (
                                        <RefreshCw size={13} className="animate-spin text-orange-400" />
                                    ) : (
                                        `Load more (${total - messages.length} more)`
                                    )}
                                </button>
                            </li>
                        )}
                    </ul>
                )}
            </div>
        </section>
    );
}
